import { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import CalendarIndividualDates from '../CalendarIndividualDates';

export default function SellItemModal({ open, setOpen }) {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    photos: [],         // same as rent modal
    title: '',
    brand: '',
    modelNumber: '',
    description: '',
    location: '',
    condition: '',
    productKeywords: ['', '', ''],
    price: '',          // new field for sell item price
    quantity: 1,
    category: '',
    pickupAvailability: {
      Monday: ['', ''],
      Tuesday: ['', ''],
      Wednesday: ['', ''],
      Thursday: ['', ''],
      Friday: ['', ''],
      Saturday: ['', ''],
      Sunday: ['', ''],
    },
    blockDates: [],
  });

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: null });
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      ['title', 'brand', 'modelNumber', 'description', 'location', 'condition'].forEach(field => {
        if (!form[field]?.toString().trim()) newErrors[field] = 'This field is required.';
      });
    } else if (step === 2) {
      if (!form.productKeywords.some(kw => kw.trim())) newErrors.productKeywords = 'At least one keyword is required.';
      if (!form.price) newErrors.price = 'Price is required.';
      if (!form.quantity || form.quantity < 1) newErrors.quantity = 'Quantity must be at least 1.';
      if (!form.category.trim()) newErrors.category = 'Category is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const inputClass = (field) =>
    `w-full p-2 border rounded ${errors[field] ? 'border-red-500' : 'border-gray-300'} focus:outline-none`;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-3xl rounded bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogTitle className="text-lg font-semibold mb-4">Create Sell Item</DialogTitle>

            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl"
            >
              &times;
            </button>

            {/* Step indicator */}
            <div className="mb-4">
              <p className="text-sm text-gray-500">Step {step} of 3</p>
              <div className="h-2 bg-gray-200 rounded-full mt-1">
                <div
                  className="h-2 rounded-full bg-blue-500 transition-all"
                  style={{ width: `${(step / 3) * 100}%` }}
                />
              </div>
            </div>

            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-4">
                {/* Photos upload can be same as RentItemModal if implemented */}

                {/* Basic info fields */}
                {['title', 'brand', 'modelNumber', 'description', 'location', 'condition'].map((field) => (
                  <div key={field}>
                    <label className="block text-sm font-medium capitalize">
                      {field === 'modelNumber' ? 'Model Number' : field}
                    </label>
                    {field === 'description' ? (
                      <textarea
                        className={inputClass(field)}
                        value={form[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                      />
                    ) : (
                      <input
                        type="text"
                        className={inputClass(field)}
                        value={form[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                      />
                    )}
                    {errors[field] && <p className="text-red-500 text-sm">{errors[field]}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-4">
                <label className="block text-sm font-medium">Product Keywords</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[0, 1, 2].map((i) => (
                    <input
                      key={i}
                      type="text"
                      className={inputClass('productKeywords')}
                      value={form.productKeywords[i]}
                      onChange={(e) => {
                        const newKeywords = [...form.productKeywords];
                        newKeywords[i] = e.target.value;
                        handleChange('productKeywords', newKeywords);
                      }}
                    />
                  ))}
                </div>
                {errors.productKeywords && <p className="text-red-500 text-sm">{errors.productKeywords}</p>}

                <div>
                  <label className="block text-sm font-medium mt-4">Price ($)</label>
                  <input
                    type="number"
                    className={inputClass('price')}
                    value={form.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                  {errors.price && <p className="text-red-500 text-sm">{errors.price}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mt-4">Quantity</label>
                  <input
                    type="number"
                    className={inputClass('quantity')}
                    value={form.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    min="1"
                  />
                  {errors.quantity && <p className="text-red-500 text-sm">{errors.quantity}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mt-4">Category</label>
                  <input
                    type="text"
                    className={inputClass('category')}
                    value={form.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                  />
                  {errors.category && <p className="text-red-500 text-sm">{errors.category}</p>}
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="space-y-4">
                <label className="block mb-1 text-sm font-medium">Pickup Availability</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.keys(form.pickupAvailability).map((day) => (
                    <div key={day}>
                      <label className="block text-sm font-medium">{day}</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="time"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={form.pickupAvailability[day][0]}
                          onChange={(e) =>
                            handleChange('pickupAvailability', {
                              ...form.pickupAvailability,
                              [day]: [e.target.value, form.pickupAvailability[day][1]],
                            })
                          }
                        />
                        <input
                          type="time"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={form.pickupAvailability[day][1]}
                          onChange={(e) =>
                            handleChange('pickupAvailability', {
                              ...form.pickupAvailability,
                              [day]: [form.pickupAvailability[day][0], e.target.value],
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium">Block Dates</label>
                  <CalendarIndividualDates
                    selectedDates={form.blockDates}
                    onSelectDates={(dates) => handleChange('blockDates', dates)}
                  />
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="mt-6 flex justify-between">
              {step > 1 && (
                <button
                  onClick={prevStep}
                  className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={nextStep}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Add Item
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
